'use client';

import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useEffect, useRef, useState } from 'react';
import { usePathname } from '@/i18n/navigation';

export interface CalendarContextValue {
  season: unknown;
  setSeason: Dispatch<SetStateAction<unknown>>;
  navigationMonths: unknown[];
  setNavigationMonths: Dispatch<SetStateAction<unknown[]>>;
  selectedDate: string;
  setSelectedDate: Dispatch<SetStateAction<string>>;
  showOverlay: boolean;
  setShowOverlay: Dispatch<SetStateAction<boolean>>;
  scrollToast: boolean;
  setScrollToast: Dispatch<SetStateAction<boolean>>;
  indicatorDate: string;
  setIndicatorDate: Dispatch<SetStateAction<string>>;
  routerChanged: boolean;
  setRouterChanged: Dispatch<SetStateAction<boolean>>;
  seasonActiveState: boolean;
  setSeasonActiveState: Dispatch<SetStateAction<boolean>>;
}

// See PrintContext for the cast-default rationale.
export const CalendarContext = createContext<CalendarContextValue>(
  undefined as unknown as CalendarContextValue,
);

const CalendarProvider = (props: { children: ReactNode }) => {
  const { children } = props;
  const pathname = usePathname();
  const firstRenderRef = useRef(true);

  const [season, setSeason] = useState<unknown>('');
  const [navigationMonths, setNavigationMonths] = useState<unknown[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [showOverlay, setShowOverlay] = useState(false);
  const [scrollToast, setScrollToast] = useState(false);
  const [indicatorDate, setIndicatorDate] = useState('');
  const [routerChanged, setRouterChanged] = useState(false);
  const [seasonActiveState, setSeasonActiveState] = useState(true);

  const scrollToPage = () => {
    setRouterChanged(true);
  };

  // App Router has no router.events('beforeHistoryChange'): a pathname change (gated on
  // scrollToast) stands in for it. Skip the initial mount so only real navigations fire.
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate effect syncing state from an external source (RHF/cookie/DOM/context/async data) — not a render-derivable value; intentional
    if (scrollToast) scrollToPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional trigger; adding the flagged deps would shift this shared provider's effect timing/identity across every consumer
  }, [pathname]);

  return (
    <CalendarContext.Provider
      value={{
        season,
        setSeason,
        navigationMonths,
        setNavigationMonths,
        selectedDate,
        setSelectedDate,
        showOverlay,
        setShowOverlay,
        setScrollToast,
        scrollToast,
        setRouterChanged,
        routerChanged,
        setIndicatorDate,
        indicatorDate,
        setSeasonActiveState,
        seasonActiveState,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export default CalendarProvider;
